import { AfterViewInit, Component, ElementRef, Inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, map, pluck, take, takeUntil, tap } from 'rxjs/operators';
import { merge, Observable, Subject } from 'rxjs';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import {
  FocusOnSearch,
  getSearchQuery,
  getSearchSuggestionsHistoryTerms,
  HideSuggestionsList,
  isSearchInputFocused,
  isSearchSuggestionsListVisible,
  LoadSearchSuggestions,
  SearchFromHeaderBar,
  SearchState,
  ShowSuggestionsList,
  StartSearch,
  UpdateSearchQuery,
} from '~app/modules/generic/store';
import { SelectSuggestionByKeyboardService } from '~app/modules/generic/services/select-suggestion-by-keyboard.service';
import { SuggestionsPopoverService } from '~app/modules/generic/services/suggestions-popover.service';
import { MobileMenuService } from '~app/modules/generic/services/mobile-menu.service';
import { ShowElementSharedService } from '~app/modules/generic/services/show-element-shared-service';
import { KeyboardUtil } from '~shared/utils/keyboard-util';
import { APP_SEARCH_ROUTE, FeatureToggleEnum, NUMBER_0, NUMBER_1, NUMBER_300 } from '~shared/constants';
import { SearchInputApplicationEventsHandler } from '~app/modules/generic/classes';
import { ApplicationSearchInteractionType } from '~shared/modules/application-event/constants';
import { getQueryParams, RouterStateUrl } from '~core/store';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { DOCUMENT } from '@angular/common';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { environment } from '~env/environment';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { MediaObserver } from '@angular/flex-layout';
import {
  GaTrackingEventAction,
  GaTrackingEventCategory,
  GaTrackingEventName,
  GATrackingEventType,
} from '~shared/modules/ga-tracking/constants';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';

@Component({
  selector: 'app-generic-header-search',
  templateUrl: './header-search.component.html',
  styleUrls: ['./header-search.component.scss'],
})
export class HeaderSearchComponent implements AfterViewInit, OnDestroy {
  @ViewChild('searchInput') searchInputRef: ElementRef;
  @ViewChild('searchField') searchField: ElementRef;
  @ViewChild('searchByPhotoInput') searchByPhotoInput: ElementRef;
  @Input() isMobile: boolean;
  MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

  searchForm: FormGroup;
  isSuggestionsListVisible$: Observable<boolean>;
  hasHistoryTerms: boolean;
  showClearIcon: boolean;
  searchBarFocused = false;
  searchTerm: string;
  isSearchByPictureEnabled$: Observable<boolean>;
  isSearchByPictureEnabledLocally: boolean;
  isShowByPictureModalVisible: boolean;
  isModalVisibilityEventTriggered: boolean;

  private minSearchLength = 1;

  private readonly destroySubject = new Subject<boolean>();
  private isFormSubmitted: boolean;
  private applicationEventHandler: SearchInputApplicationEventsHandler;

  constructor(
    private store: Store<SearchState>,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private suggestionsPopoverService: SuggestionsPopoverService,
    private selectSuggestionByKeyboardService: SelectSuggestionByKeyboardService,
    private mobileMenuService: MobileMenuService,
    private showElementSharedService: ShowElementSharedService,
    // Not private because we don't access it anywhere besides the constructor
    applicationEventService: ApplicationEventService,
    private routerStore: Store<RouterStateUrl>,
    @Inject(DOCUMENT) private document: Document,
    private activeFeaturesConfigCatService: ActiveFeaturesConfigcatService,
    private mediaObserver: MediaObserver,
    private gtmDatalayerService: GtmDatalayerService
  ) {
    this.isSearchByPictureEnabled$ = this.activeFeaturesConfigCatService.isFeatureEnabledReadyForCurrentCountry$(
      FeatureToggleEnum.FF_SEARCH_PRODUCTS_BY_PHOTO
    );
    this.applicationEventHandler = new SearchInputApplicationEventsHandler(applicationEventService);
    this.isSuggestionsListVisible$ = this.store.pipe(select(isSearchSuggestionsListVisible));

    this.store
      .pipe(
        select(getSearchSuggestionsHistoryTerms),
        map((historyTerms) => historyTerms && 0 < historyTerms.length),
        takeUntil(this.destroySubject)
      )
      .subscribe((hasHistoryTerms) => {
        this.hasHistoryTerms = hasHistoryTerms;
      });

    this.createSearchFormAndSubscribeToInputChanges();
    this.listenToUpdateSearchTerm();
    this.route.queryParamMap.pipe(pluck('params', 'q')).subscribe((param) => {
      this.showClearIcon = !!param;
    });
  }

  ngAfterViewInit() {
    this.routerStore
      .pipe(select(getQueryParams), take(1), takeUntil(this.destroySubject))
      .subscribe((params: Params) => {
        if (!!localStorage?.getItem('show-camera-search-icon')) {
          this.isSearchByPictureEnabledLocally = !!localStorage?.getItem('show-camera-search-icon');
          if (params?.activatePhotoSearch) {
            if (params?.activatePhotoSearch === 'false') {
              localStorage?.removeItem('show-camera-search-icon');
              this.isSearchByPictureEnabledLocally = false;
            }
          }
        } else {
          if (params?.activatePhotoSearch) {
            if (params?.activatePhotoSearch === 'true') {
              localStorage?.setItem('show-camera-search-icon', 'true');
              this.isSearchByPictureEnabledLocally = true;
            }
          }
        }
      });

    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.destroySubject))
      .subscribe((foo) => {
        if (!this.mediaObserver.isActive(MEDIA_ALISES.mobile)) {
          this.suggestionsPopoverService.initHeaderSearch(this.searchInputRef.nativeElement);
        }
      });

    this.store.pipe(select(isSearchInputFocused), takeUntil(this.destroySubject)).subscribe((isFocused) => {
      if (isFocused) {
        this.searchField.nativeElement.focus();
      }
      if (this.isMobile) {
        this.document.body.classList.toggle('search-noScroll', isFocused);
      }
    });
  }

  ngOnDestroy() {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  onKeydown(event) {
    event.stopImmediatePropagation();

    const suggestionsIsSelected = this.selectSuggestionByKeyboardService.isSsgSelected();

    if (KeyboardUtil.isEnter(event) && suggestionsIsSelected) {
      // If we select a suggestion, we should blur from our search input
      this.searchField.nativeElement.blur();
      this.selectSuggestionByKeyboardService.pushKeyboardUpdate(event);
    } else if (KeyboardUtil.isEnter(event) && !suggestionsIsSelected) {
      // If there is no selected suggestion and user clicked Enter, do normal submit with the input value
      this.onSubmit();
      this.searchBarFocused = false;
    } else {
      this.selectSuggestionByKeyboardService.pushKeyboardUpdate(event);
    }
  }

  onFocus() {
    const term = this.searchForm.controls['searchTerm'].value || '';
    this.searchBarFocused = true;

    this.closeMobileMenu();
    this.updateInputSearchTerm(term);
    this.store.dispatch(new FocusOnSearch(true));
    this.store.dispatch(new UpdateSearchQuery(term));
    if (term && term.length) {
      this.loadSuggestions(term);
    } else if (this.hasHistoryTerms) {
      this.store.dispatch(new ShowSuggestionsList());
    }

    this.showElementSharedService.showStickyAddToCartOnPdp(false);
    this.applicationEventHandler.fireSearchInteractionApplicationEvent(
      ApplicationSearchInteractionType.ClickInTheSearchBar
    );
  }

  onBlur() {
    this.store.dispatch(new FocusOnSearch(false));
    if (this.isFormSubmitted) {
      this.isFormSubmitted = false;
      return;
    }

    this.showElementSharedService.showStickyAddToCartOnPdp(true);
    this.applicationEventHandler.fireSearchInteractionApplicationEvent(
      ApplicationSearchInteractionType.BounceOfSearchBar
    );
    this.searchBarFocused = false;
  }

  onSubmit() {
    if (this.searchForm.valid) {
      let pageNumberWhenUserSearchedFromHeaderBar = NUMBER_1;
      const searchTerm = this.searchForm.controls['searchTerm'].value;

      this.routerStore
        .pipe(select(getQueryParams), take(1), takeUntil(this.destroySubject))
        .subscribe((params: Params) => {
          const offset = params.offset;
          const limit = params.limit;

          if (offset && limit) {
            pageNumberWhenUserSearchedFromHeaderBar = UrlUtils.getCurrentPage(offset, limit);
          }

          this.store.dispatch(
            new SearchFromHeaderBar({
              isSearchFromHeaderBar: true,
              pageNumberWhenUserSearchedFromHeaderBar,
            })
          );

          this.store.dispatch(new StartSearch({ value: searchTerm }));
        });
    }

    this.isFormSubmitted = true;
    this.searchField.nativeElement.blur();
    this.store.dispatch(new HideSuggestionsList());
  }

  private createSearchFormAndSubscribeToInputChanges() {
    this.searchForm = this.formBuilder.group({
      searchTerm: ['', [Validators.required, Validators.minLength(this.minSearchLength)]],
    });

    this.searchForm
      .get('searchTerm')
      .valueChanges.pipe(
        tap((term) => this.store.dispatch(new UpdateSearchQuery(term))),
        tap((term) => (this.showClearIcon = term.length > 0)),
        debounceTime(NUMBER_300),
        distinctUntilChanged(),
        takeUntil(this.destroySubject),
        filter((term: string) => term?.length >= NUMBER_0)
      )
      .subscribe((term: string) => {
        this.searchTerm = term;
        this.onSearchInputChange(term);
      });
  }

  private onSearchInputChange(term: string): void {
    const termLength = (term && term?.length) || 0;
    if (!termLength && this.hasHistoryTerms) {
      this.store.dispatch(new ShowSuggestionsList());
    } else if (this.minSearchLength <= termLength) {
      this.loadSuggestions(term);
    } else {
      this.store.dispatch(new HideSuggestionsList());
    }
  }

  private listenToUpdateSearchTerm() {
    merge(this.store.pipe(select(getSearchQuery)), this.route.queryParamMap.pipe(pluck('params', 'q')))
      .pipe(distinctUntilChanged(), takeUntil(this.destroySubject))
      .subscribe((term: string) => {
        this.searchTerm = term;
        this.updateInputSearchTerm(term);
      });
  }

  private updateInputSearchTerm(searchTerm) {
    this.searchForm.patchValue({ searchTerm }, { emitEvent: false });
  }

  private loadSuggestions(term: string): void {
    this.store.dispatch(new LoadSearchSuggestions({ term }));
  }

  private closeMobileMenu() {
    this.mobileMenuService.close();
  }

  clearSearch(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.searchForm.controls['searchTerm'].setValue('');
    this.searchField.nativeElement.focus();
    this.store.dispatch(new FocusOnSearch(true));
  }

  onPhotoSearchClick(event) {
    event.stopPropagation();
    event.preventDefault();
    this.searchByPhotoInput.nativeElement.click();
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.SE_IMAGE_SEARCH_LOGO_CLICKED,
      GA_eventname: GaTrackingEventName.Search,
      action: GaTrackingEventAction.IMAGE_SEARCH_LOGO_CLICKED,
      category: GaTrackingEventCategory.ImageSearch,
    });
  }
  showSearchByPhotoModal(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    if (this.isMobile) {
      return;
    }
    this.isShowByPictureModalVisible = true;
    if (!this.isModalVisibilityEventTriggered) {
      this.gtmDatalayerService.gtagEvent({
        event: GATrackingEventType.SE_HOVER_IMAGE_SEARCH,
        GA_eventname: GaTrackingEventName.Search,
        action: GaTrackingEventAction.HOVER_IMAGE_SEARCH,
        category: GaTrackingEventCategory.ImageSearch,
      });
      this.isModalVisibilityEventTriggered = true;
    }
  }
  hideSearchByPhotoModal(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.isShowByPictureModalVisible = false;
    this.isModalVisibilityEventTriggered = false;
  }
  handlePhotoSearch(event) {
    const file = event.target.files?.[0];
    this.handleFileChange(file);
  }

  handleFileChange(file) {
    if (file && !(file.size > this.MAX_FILE_SIZE)) {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const timeStamp = Date.now();
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          canvas.width = 300;
          canvas.height = 300;
          ctx.drawImage(img, 0, 0, 300, 300);

          const resizedBase64 = canvas.toDataURL('image/jpeg', 0.8);
          clearSessionStorageByPrefix('photoSearched');
          sessionStorage.setItem(`photoSearched-${timeStamp}`, resizedBase64);
          window.location.href = `${environment.appBaseUrl()}/${APP_SEARCH_ROUTE}?searchByPhoto=${timeStamp}`;
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }
}

const clearSessionStorageByPrefix = (prefix) => {
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(prefix)) {
      sessionStorage.removeItem(key);
    }
  }
};
